/**
 * The Activity chart must show the platform's full channel catalogue, rather
 * than treating an empty channel as if it did not exist. A zero is useful
 * information here: it tells the observer that the channel was considered.
 */

export function surveyCount(value) {
    const count = Number(value);
    return Number.isFinite(count) && count > 0 ? Math.round(count) : 0;
}

/**
 * Round an axis ceiling to the familiar 1 / 2 / 5 sequence. It keeps small
 * test sends legible without turning a large production run into a bar that
 * overflows the panel.
 */
export function chartScaleCeiling(values = []) {
    const largest = values.reduce((highest, value) => Math.max(highest, surveyCount(value)), 0);
    if (largest === 0) return 1;

    const magnitude = 10 ** Math.floor(Math.log10(largest));
    const leading = largest / magnitude;
    if (leading <= 1) return magnitude;
    if (leading <= 2) return 2 * magnitude;
    if (leading <= 5) return 5 * magnitude;
    return 10 * magnitude;
}

export function formatSurveyCount(value) {
    return new Intl.NumberFormat('en-US').format(surveyCount(value));
}

export function buildActivityChart(channelCatalogue = [], sends = []) {
    const received = new Map();
    for (const send of sends) {
        if (!send?.channel) continue;
        received.set(send.channel, send);
    }

    const rows = channelCatalogue.map((channel) => {
        const evidenceChannel = channel.bindingChannel || channel.id;
        const send = received.get(evidenceChannel) || {};
        received.delete(evidenceChannel);
        return {
            channel: channel.id,
            name: channel.name,
            color: channel.markColor,
            surveys: surveyCount(send.surveys),
            messages: surveyCount(send.messages),
        };
    });

    // Do not hide a future channel unknown to this version of the browser.
    for (const [channel, send] of received) {
        rows.push({
            channel,
            name: channel,
            color: 'var(--color-categorical-unpaletted)',
            surveys: surveyCount(send.surveys),
            messages: surveyCount(send.messages),
        });
    }

    const ceiling = chartScaleCeiling(rows.map((row) => row.surveys));
    return {
        ceiling,
        rows: rows.map((row) => ({
            ...row,
            percent: Math.min(100, (row.surveys / ceiling) * 100),
        })),
    };
}
